import { useEffect } from 'react';

/** Wheel events from here rightward drive the panel, the right 75%. */
const CAPTURE_FROM = 0.25;
const capturesAt = (x) => x >= window.innerWidth * CAPTURE_FROM;

/** Fraction of the remaining distance covered each frame. */
const EASE = 0.18;

/**
  * Widens a panel's scrollable area to the right 75% of the page.
  *
  * The panels sit in the right column and the body itself never scrolls, so a
  * wheel event over the particle field would otherwise do nothing. This forwards
  * those events to the active panel. The leftmost quarter is left alone so the
  * nav rail and wordmark keep their own space.
  *
  * The wheel delta accumulates into a target and the panel eases toward it each
  * frame, rather than being written straight to scrollTop. Assigning directly
  * lands the whole delta in one paint, which reads as stiff next to the browser's
  * own animated wheel scrolling when the pointer is over the panel.
  *
  * Scrolling only, hovering still requires the pointer to be over a row.
  */
export default function useWheelScroll(ref, active) {
    useEffect(() => {
        if (!active) return undefined;

        let raf = 0;
        let target = null; // null when idle, so the next wheel re-reads scrollTop

        const step = () => {
            const el = ref.current;
            if (!el || target == null) {
                raf = 0;
                return;
            }
            const diff = target - el.scrollTop;
            if (Math.abs(diff) < 0.5) {
                el.scrollTop = target;
                target = null;
                raf = 0;
                return;
            }
            el.scrollTop += diff * EASE;
            raf = requestAnimationFrame(step);
        };

        const onWheel = (e) => {
            const el = ref.current;
            if (!el) return;
            // Already over the panel: the browser scrolls it natively, and handling it
            // here as well would move it twice as far. Abandon any glide still in
            // flight from outside the panel, it would keep writing scrollTop toward
            // its old target and fight the native scroll to a standstill.
            if (el.contains(e.target)) {
                if (raf) {
                    cancelAnimationFrame(raf);
                    raf = 0;
                }
                target = null;
                return;
            }
            if (!capturesAt(e.clientX)) return;

            // deltaMode: 0 = pixels, 1 = lines, 2 = pages.
            const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? el.clientHeight : 1;
            // Mid-glide, keep accumulating onto the pending target so a fast burst of
            // ticks travels the full distance instead of restarting from where the
            // animation happens to be.
            const from = target == null ? el.scrollTop : target;
            const max = el.scrollHeight - el.clientHeight;
            target = Math.max(0, Math.min(max, from + e.deltaY * unit));

            if (!raf) raf = requestAnimationFrame(step);
        };

        window.addEventListener('wheel', onWheel, { passive: true });
        return () => {
            window.removeEventListener('wheel', onWheel);
            cancelAnimationFrame(raf);
        };
    }, [ref, active]);
}
